import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LayoutList, Filter } from 'lucide-react';
import api from '../../../../services/api';
import LoadingSpinner from '../../../common/LoadingSpinner';

const AttendanceOverview = ({ basePath = '/admin/attendance' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const { month, year } = filters;
      const response = await api.get('/admin/attendance/overview', {
        params: { month, year },
      });
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching attendance overview:', error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: parseInt(value, 10),
    }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-2xl shadow-xl p-8 text-white">
        <button
          onClick={() => navigate(basePath)}
          className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white text-slate-800 border border-slate-200 shadow-md hover:shadow-lg hover:bg-slate-50 transition-all duration-200 mb-5 group"
          aria-label="Back to Attendance"
        >
          <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200" />
          <span className="font-medium">Back to Attendance</span>
        </button>

        <div className="flex items-center space-x-4">
          <div className="bg-slate-600 bg-opacity-40 rounded-full p-3">
            <LayoutList className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Attendance Overview</h1>
            <p className="text-slate-200 mt-1">
              Monthly consolidated Full Days, Half Days, and Absent counts per employee
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-wrap items-end gap-4">
          <Filter className="h-5 w-5 text-gray-400 mb-2" />
          <div>
            <label htmlFor="overview-month" className="block text-sm font-medium text-gray-700">
              Month
            </label>
            <select
              id="overview-month"
              name="month"
              value={filters.month}
              onChange={handleFilterChange}
              className="mt-1 block w-full min-w-[10rem] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="overview-year" className="block text-sm font-medium text-gray-700">
              Year
            </label>
            <select
              id="overview-year"
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              className="mt-1 block w-full min-w-[8rem] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner size="lg" className="py-8" />
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Overview —{' '}
              {new Date(0, filters.month - 1).toLocaleString('default', { month: 'long' })}{' '}
              {filters.year}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Counts from attendance records only ({employees.length} employees)
            </p>
          </div>
          <div className="overflow-x-auto">
            {employees.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500">No employees found</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-white uppercase tracking-wider">
                      Full Days
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-white uppercase tracking-wider">
                      Half Days
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-white uppercase tracking-wider">
                      Absent
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employees.map((emp, index) => (
                    <tr
                      key={emp.employeeId || emp.name || index}
                      className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                    >
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {emp.name}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                        {emp.fullDays}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                        {emp.halfDays}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                        {emp.absent}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceOverview;
